import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useGetPermissionsQuery,
  useGetRoleByIdQuery,
  useUpdateRoleMutation,
} from "../../infrastructure";
import type { PermissionGroup } from "../../infrastructure/api/mapper";
import { toast } from "sonner";

interface EditRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string | null;
  onSuccess: () => void;
}

interface CategoryPermission {
  id: string;
  slug: string;
  generic_name?: string;
  description?: string;
}

interface Category {
  category: string;
  permissions: CategoryPermission[];
}

export const EditRoleDialog: React.FC<EditRoleDialogProps> = ({
  open,
  onOpenChange,
  roleId,
  onSuccess,
}) => {
  const { t } = useTranslation("rolesPermissions");
  const [selectedPermissions, setSelectedPermissions] = React.useState<
    string[]
  >([]);
  const [roleName, setRoleName] = React.useState("");

  // Fetch all available permissions
  const { data: allPermissions, isLoading: isLoadingPermissions } =
    useGetPermissionsQuery();

  // Fetch role details to get current permissions
  const { data: roleData, isLoading: isLoadingRole } = useGetRoleByIdQuery(
    roleId!,
    {
      skip: !roleId,
    },
  );

  // Update mutation
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();

  // Initialize selected permissions and role name when role data loads
  React.useEffect(() => {
    if (roleData?.permissions) {
      // Handle both name (from list API) and slug (from role by ID API)
      setSelectedPermissions(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        roleData.permissions.map((p: any) => p.id || p.slug || p.name),
      );
    }
    if (roleData?.name) {
      setRoleName(roleData.name);
    }
  }, [roleData]);

  // Transform all permissions to grouped format
  const permissionGroups: PermissionGroup[] = React.useMemo(() => {
    const categories = allPermissions as Category[] | undefined;
    if (!categories) return [];

    return categories.map((cat) => ({
      title: cat.category,
      items: cat.permissions.map((p) => ({
        id: p.id,
        label: p.generic_name || p.slug,
        description: p.description,
      })),
    }));
  }, [allPermissions]);

  // Handle category checkbox
  const handleCategoryChange = (title: string, checked: boolean) => {
    const group = permissionGroups.find((g) => g.title === title);
    if (!group) return;

    const groupIds = group.items.map((item) => item.id);

    setSelectedPermissions((prev) => {
      if (checked) {
        // Add all permissions from this category
        const newSelected = [...prev];
        groupIds.forEach((id) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      } else {
        // Remove all permissions from this category
        return prev.filter((id) => !groupIds.includes(id));
      }
    });
  };

  // Handle individual permission checkbox
  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    setSelectedPermissions((prev) => {
      if (checked) {
        return [...prev, permissionId];
      } else {
        return prev.filter((id) => id !== permissionId);
      }
    });
  };

  // Get category checked state (true, false, or indeterminate)
  // Uses the selectedSet memo defined below the return — hoisted via closure
  const getCategoryState = (
    title: string,
    set: Set<string>,
  ): "checked" | "unchecked" | "indeterminate" => {
    const group = permissionGroups.find((g) => g.title === title);
    if (!group) return "unchecked";

    const groupIds = group.items.map((item) => item.id);
    const selectedCount = groupIds.filter((id) => set.has(id)).length;

    if (selectedCount === 0) return "unchecked";
    if (selectedCount === groupIds.length) return "checked";
    return "indeterminate";
  };

  const handleSave = async () => {
    if (!roleId) return;

    try {
      await updateRole({
        roleId,
        name: roleName,
        permission_ids: selectedPermissions,
      }).unwrap();

      toast.success(t("editRoleDialog.success"));
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error(t("editRoleDialog.error"));
    }
  };

  const isLoading = isLoadingPermissions || isLoadingRole;

  // Memoised Set for O(1) checkbox lookups instead of O(n) Array.includes
  const selectedSet = React.useMemo(
    () => new Set(selectedPermissions),
    [selectedPermissions],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100dvh-1rem)] flex-col overflow-hidden p-0 sm:h-[80vh] sm:max-w-[650px]">
        {/* Fixed header */}
        <DialogHeader className="px-6 pt-5 pb-4 flex-shrink-0 border-b">
          <DialogTitle className="text-xl font-semibold">
            {t("editRoleDialog.title", { name: roleData?.name ?? "" })}
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable permissions area */}
        {isLoading ? (
          <p className="text-gray-500 py-4 px-6">{t("editRoleDialog.loading")}</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-6">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">{t("editRoleDialog.nameLabel")}</label>
              <Input
                placeholder={t("editRoleDialog.namePlaceholder")}
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
              />
            </div>
            {permissionGroups.map((group) => {
              const categoryState = getCategoryState(group.title, selectedSet);
              const isIndeterminate = categoryState === "indeterminate";
              return (
                <div key={group.title}>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() =>
                        handleCategoryChange(
                          group.title,
                          !isIndeterminate && categoryState !== "checked",
                        )
                      }
                      className={`w-4 h-4 border rounded flex items-center justify-center ${
                        categoryState === "checked"
                          ? "bg-primary border-primary"
                          : isIndeterminate
                            ? "bg-primary border-primary"
                            : "border-gray-300"
                      }`}
                    >
                      {categoryState === "checked" && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                      {isIndeterminate && (
                        <div className="w-3 h-0.5 bg-white" />
                      )}
                    </button>
                    <h3 className="font-medium text-gray-800">{group.title}</h3>
                  </div>
                  {group.items.length > 0 ? (
                    <div className="flex flex-wrap gap-3 ml-6">
                      {group.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedSet.has(item.id)}
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                item.id,
                                checked as boolean,
                              )
                            }
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white"
                          />
                          {item.description ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm cursor-default">
                                  {item.label}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                {item.description}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-sm">{item.label}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 ml-6">
                      {t("editRoleDialog.noPermissionsInCategory")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Fixed footer */}
        <DialogFooter className="flex items-center">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-primary text-primary bg-white hover:bg-white py-4 px-4 hover:text-primary cursor-pointer mb-4 "
          >
            {t("editRoleDialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            loading={isUpdating}
            disabled={!roleName.trim()}
            className="bg-primary border-primary hover:bg-primary cursor-pointer py-4 px-4 mb-4 mr-2"
          >
            {t("editRoleDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
